import { z } from 'zod';

// Player profile rules, shared by the browser (instant feedback) and the API (the real check), so the
// two can never disagree.

// ----- age -----
export const MINIMUM_AGE = 18;
const DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

// today's date in the UK, as YYYY-MM-DD: the age rule follows UK days, not the server's or the browser's
export function todayInUk(now = new Date()) {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/London' }).format(now);
}

// a real calendar date, written YYYY-MM-DD
export function isRealDate(value: string) {
  const match = DATE.exec(value);
  if (!match) return false;
  const [, y, m, d] = match.map(Number) as [number, number, number, number];
  const date = new Date(Date.UTC(y, m - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d;
}

// whole years between a date of birth and a day. Someone born on 29 February turns a year older on
// 1 March in years without a 29 February, as UK law treats it.
export function ageOn(dateOfBirth: string, day: string) {
  const [by, bm, bd] = dateOfBirth.split('-').map(Number) as [number, number, number];
  const [ty, tm, td] = day.split('-').map(Number) as [number, number, number];
  const beforeBirthday = tm < bm || (tm === bm && td < bd);
  return ty - by - (beforeBirthday ? 1 : 0);
}

export type DateOfBirthCheck = { ok: true; age: number } | { ok: false; reason: 'invalid' | 'future' | 'under_age' };
export function checkDateOfBirth(dateOfBirth: string, today = todayInUk()): DateOfBirthCheck {
  if (!isRealDate(dateOfBirth) || dateOfBirth < '1900-01-01') return { ok: false, reason: 'invalid' };
  if (dateOfBirth > today) return { ok: false, reason: 'future' };
  const age = ageOn(dateOfBirth, today);
  return age >= MINIMUM_AGE ? { ok: true, age } : { ok: false, reason: 'under_age' };
}
export const DATE_OF_BIRTH_MESSAGES = {
  invalid: 'Enter a real date of birth.',
  future: 'Your date of birth cannot be in the future.',
  under_age: `London Life is for adults aged ${MINIMUM_AGE} and over.`,
} as const;

// ----- usernames -----
// 3 to 20 letters, numbers or underscores, with at least one letter. Shown as typed (@Jerry_O), compared
// without capitals, so @jerry_o is the same name.
export const USERNAME_PATTERN = /^(?=.*[A-Za-z])[A-Za-z0-9_]{3,20}$/;
export const USERNAME_CHANGE_DAYS = 30;
export const usernameKey = (username: string) => username.toLowerCase();

// names that would look official or could be used to mislead other players
const RESERVED = [
  'admin',
  'administrator',
  'moderator',
  'mod',
  'staff',
  'support',
  'help',
  'helpdesk',
  'official',
  'system',
  'londonlife',
  'london_life',
  'londonlifegame',
  'team',
  'security',
  'privacy',
  'legal',
  'police',
  'gov',
  'government',
  'root',
  'null',
  'undefined',
  'anonymous',
  'everyone',
  'here',
  'whogocode',
];
// words never allowed anywhere in a name; the admin will manage a longer list in step 3d
const BLOCKED_PARTS = [
  'fuck',
  'shit',
  'cunt',
  'nigger',
  'nigga',
  'faggot',
  'retard',
  'rapist',
  'nazi',
  'hitler',
  'paedo',
  'pedo',
  'whore',
  'slut',
];

// true when any blocked word appears in the text, ignoring capitals, spaces and punctuation; used for every
// name a player types (usernames, character names, self-described gender)
export function containsBlockedWord(text: string): boolean {
  const plain = text.toLowerCase().replace(/[^\p{L}]/gu, '');
  return BLOCKED_PARTS.some((part) => plain.includes(part));
}

export type UsernameCheck = { ok: true } | { ok: false; reason: 'format' | 'reserved' | 'blocked' };
export function checkUsername(username: string): UsernameCheck {
  if (!USERNAME_PATTERN.test(username)) return { ok: false, reason: 'format' };
  const key = usernameKey(username);
  const plain = key.replace(/[_0-9]/g, '');
  if (RESERVED.includes(key) || RESERVED.includes(plain) || /^(admin|mod|staff|support|official)_/.test(key))
    return { ok: false, reason: 'reserved' };
  if (containsBlockedWord(plain)) return { ok: false, reason: 'blocked' };
  return { ok: true };
}
export const USERNAME_MESSAGES = {
  format: 'Use 3 to 20 letters, numbers or underscores, with at least one letter.',
  reserved: 'That name is reserved. Please choose another.',
  blocked: 'That name is not allowed. Please choose another.',
  taken: 'That name is taken. Please choose another.',
} as const;

// ----- legal documents -----
// Each version is stored with every acceptance. Changing a version here asks every player to accept the
// new one before playing on. 'draft' shows a banner on the pages until Jerry has approved the text.
export const LEGAL_STATUS: 'draft' | 'final' = 'final';
export const LEGAL_DOCUMENTS = {
  terms: { title: 'Terms of Service', path: '/terms', version: '2026-10-09' },
  privacy: { title: 'Privacy Policy', path: '/privacy', version: '2026-10-09' },
  disclaimer: { title: 'Disclaimer', path: '/disclaimer', version: '2026-10-09' },
} as const;
export type LegalDocument = keyof typeof LEGAL_DOCUMENTS;
export const LEGAL_DOCUMENT_KEYS = Object.keys(LEGAL_DOCUMENTS) as LegalDocument[];

// ----- requests and replies -----
export const ProfileStatus = z.enum(['complete', 'incomplete', 'age_restricted']);
export type ProfileStatus = z.infer<typeof ProfileStatus>;
export const PlayerProfile = z.object({ username: z.string().nullable(), status: ProfileStatus });
export type PlayerProfile = z.infer<typeof PlayerProfile>;

export const ProfileSetup = z
  .object({
    username: z
      .string()
      .trim()
      .refine((value) => checkUsername(value).ok, USERNAME_MESSAGES.format),
    dateOfBirth: z.string().refine(isRealDate, DATE_OF_BIRTH_MESSAGES.invalid),
    // the versions the player saw and agreed to; the API refuses anything but the current ones
    accepted: z.object({ terms: z.string(), privacy: z.string(), disclaimer: z.string() }).strict(),
    adult: z.literal(true),
  })
  .strict();
export type ProfileSetup = z.infer<typeof ProfileSetup>;

export const UsernameAvailability = z.object({
  available: z.boolean(),
  reason: z.enum(['format', 'reserved', 'blocked', 'taken']).optional(),
});
export type UsernameAvailability = z.infer<typeof UsernameAvailability>;
